import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { refreshUserFromDB } from '@/lib/customAuth';
import { hashPassword, verifyPassword } from '@/lib/hashUtils';

interface TransferRecord {
  id: string;
  delta: number;
  source: string;
  remark: string | null;
  balance_after: number;
  created_at: string;
}

interface PayPasswordModalProps {
  onConfirm: (pwd: string) => void;
  onCancel: () => void;
  loading: boolean;
  error: string;
}

const PayPasswordModal = ({ onConfirm, onCancel, loading, error }: PayPasswordModalProps) => {
  const [pwd, setPwd] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={onCancel}>
      <div
        className="w-full max-w-[480px] bg-white rounded-t-2xl px-6 pt-6 pb-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-900 text-base">输入支付密码</h3>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-gray-400 text-xl" />
          </button>
        </div>
        <input
          type="password"
          maxLength={20}
          placeholder="请输入支付密码"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-rose-300 mb-2"
          autoFocus
        />
        {error && <p className="text-xs text-rose-500 mb-3">{error}</p>}
        <button
          onClick={() => onConfirm(pwd)}
          disabled={!pwd || loading}
          className={`w-full py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap mt-3 ${
            pwd && !loading ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-400'
          }`}
        >
          {loading ? '处理中...' : '确认转账'}
        </button>
      </div>
    </div>
  );
};

const TransferPage = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const userPoints = user?.points ?? 0;

  const [form, setForm] = useState({ targetPhone: '', amount: '' });
  const [showPayModal, setShowPayModal] = useState(false);
  const [payError, setPayError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successInfo, setSuccessInfo] = useState({ phone: '', amount: '' });
  const [formError, setFormError] = useState('');

  // 收款方实时识别
  const [targetNickname, setTargetNickname] = useState('');
  const [targetSearching, setTargetSearching] = useState(false);
  const [targetNotFound, setTargetNotFound] = useState(false);

  const [showRecords, setShowRecords] = useState(false);
  const [records, setRecords] = useState<TransferRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  // 手机号输入后实时查询收款方昵称
  useEffect(() => {
    const phone = form.targetPhone.trim();
    setTargetNickname('');
    setTargetNotFound(false);
    if (phone.length < 11) return;
    setTargetSearching(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('custom_users')
        .select('id, nickname')
        .eq('phone', phone)
        .maybeSingle();
      setTargetSearching(false);
      if (data && data.id !== user?.id) {
        setTargetNickname(data.nickname || phone);
        setTargetNotFound(false);
      } else {
        setTargetNickname('');
        setTargetNotFound(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.targetPhone, user?.id]);

  // 加载转账记录（查 source 为 transfer_in 或 transfer_out 的记录）
  useEffect(() => {
    if (!showRecords || !user?.id) return;
    setRecordsLoading(true);
    supabase
      .from('points_log')
      .select('id, delta, source, remark, balance_after, created_at')
      .eq('user_id', user.id)
      .in('source', ['transfer_in', 'transfer_out', 'transfer'])
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        setRecords((data as TransferRecord[]) ?? []);
        setRecordsLoading(false);
      });
  }, [showRecords, user?.id]);

  const handleOpenPayModal = () => {
    setFormError('');
    const amt = Number(form.amount);
    if (!form.targetPhone.trim()) {
      setFormError('请输入对方手机号');
      return;
    }
    if (!form.amount || isNaN(amt) || amt <= 0) {
      setFormError('请输入有效的转账积分数量');
      return;
    }
    if (amt > userPoints) {
      setFormError('积分余额不足');
      return;
    }
    if (!Number.isInteger(amt)) {
      setFormError('积分数量必须为整数');
      return;
    }
    setPayError('');
    setShowPayModal(true);
  };

  const handleConfirmTransfer = async (payPwd: string) => {
    if (!user?.id) return;
    setSubmitting(true);
    setPayError('');

    try {
      const amt = Number(form.amount);

      // 1. 查找对方用户（按手机号）
      const { data: targetData, error: targetErr } = await supabase
        .from('custom_users')
        .select('id, nickname, phone, points')
        .eq('phone', form.targetPhone.trim())
        .maybeSingle();

      if (targetErr || !targetData) {
        setPayError('未找到该手机号对应的用户');
        setSubmitting(false);
        return;
      }

      if (targetData.id === user.id) {
        setPayError('不能向自己转账');
        setSubmitting(false);
        return;
      }

      // 2. 验证支付密码
      const { data: selfData, error: selfErr } = await supabase
        .from('custom_users')
        .select('points, pay_password')
        .eq('id', user.id)
        .maybeSingle();

      if (selfErr || !selfData) {
        setPayError('获取用户信息失败，请重试');
        setSubmitting(false);
        return;
      }

      if (!selfData.pay_password) {
        setPayError('您尚未设置支付密码，请先在设置中设置');
        setSubmitting(false);
        return;
      }

      const { valid: pwdValid, isLegacy } = await verifyPassword(payPwd, selfData.pay_password);
      if (!pwdValid) {
        setPayError('支付密码错误');
        setSubmitting(false);
        return;
      }

      // 如果是旧格式密码，自动迁移为新格式
      if (isLegacy) {
        const newHash = await hashPassword(payPwd);
        await supabase.from('custom_users').update({ pay_password: newHash }).eq('id', user.id);
      }

      // 3. 再次校验余额（以数据库为准）
      if (selfData.points < amt) {
        setPayError('积分余额不足');
        setSubmitting(false);
        return;
      }

      const newSelfPoints = selfData.points - amt;
      const newTargetPoints = (targetData.points ?? 0) + amt;

      // 4. 扣减自己积分（带 select 验证是否真的更新成功）
      const { error: deductErr, data: deductData } = await supabase
        .from('custom_users')
        .update({ points: newSelfPoints })
        .eq('id', user.id)
        .select('id, points');

      if (deductErr || !deductData || deductData.length === 0) {
        setPayError(`扣减积分失败，请重试（${deductErr?.message ?? '未匹配到记录'}）`);
        setSubmitting(false);
        return;
      }

      // 5. 增加对方积分（带 select 验证）
      const { error: addErr, data: addData } = await supabase
        .from('custom_users')
        .update({ points: newTargetPoints })
        .eq('id', targetData.id)
        .select('id, points');

      if (addErr || !addData || addData.length === 0) {
        // 回滚自己的积分
        await supabase.from('custom_users').update({ points: selfData.points }).eq('id', user.id);
        setPayError(`增加对方积分失败，已回滚（${addErr?.message ?? '未匹配到记录'}）`);
        setSubmitting(false);
        return;
      }

      // 6. 写转出日志
      const { error: logOutErr } = await supabase.from('points_log').insert({
        user_id: user.id,
        delta: -amt,
        type: 'expense',
        source: 'transfer_out',
        remark: `转账给 ${targetData.nickname || targetData.phone}`,
        balance_after: newSelfPoints,
      });
      if (logOutErr) console.warn('写转出日志失败:', logOutErr.message);

      // 7. 写转入日志
      const { error: logInErr } = await supabase.from('points_log').insert({
        user_id: targetData.id,
        delta: amt,
        type: 'income',
        source: 'transfer_in',
        remark: `收到来自 ${user.nickname} 的转账`,
        balance_after: newTargetPoints,
      });
      if (logInErr) console.warn('写转入日志失败:', logInErr.message);

      // 8. 刷新本地用户数据（积分同步）
      await refreshUserFromDB();
      refresh();

      setSuccessInfo({ phone: form.targetPhone.trim(), amount: form.amount });
      setShowPayModal(false);
      setSubmitted(true);
    } catch {
      setPayError('网络异常，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setForm({ targetPhone: '', amount: '' });
    setFormError('');
    setRecords([]);
    setShowRecords(false);
  };

  const formatDate = (ts: string) => {
    const d = new Date(ts);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 max-w-[480px] mx-auto">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center px-4 h-14 gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-xl" />
          </button>
          <h1 className="font-bold text-gray-900 text-base">我要转账</h1>
        </div>
      </header>

      {submitted ? (
        <div className="flex flex-col items-center justify-center py-20 px-8">
          <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
            <i className="ri-check-line text-emerald-500 text-3xl" />
          </div>
          <p className="font-bold text-gray-900 text-lg mb-2">转账成功！</p>
          <p className="text-gray-500 text-sm text-center mb-6">
            已向 <span className="text-gray-800 font-medium">{successInfo.phone}</span> 转账{' '}
            <span className="text-rose-500 font-bold">{Number(successInfo.amount).toLocaleString()}</span> 积分
          </p>
          <button
            onClick={handleReset}
            className="w-full bg-rose-500 text-white py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            再次转账
          </button>
          <button
            onClick={() => navigate('/wallet')}
            className="w-full mt-3 border border-gray-200 text-gray-600 py-3 rounded-full text-sm cursor-pointer whitespace-nowrap"
          >
            返回我的积分
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-3">
          {/* Points hint */}
          <div className="bg-rose-50 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">可转积分余额</span>
            <span className="text-rose-500 font-bold text-lg">{userPoints.toLocaleString()} 积分</span>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-4 space-y-4">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">转账类型</p>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
                <i className="ri-copper-coin-line text-rose-500" />
                <span className="text-sm text-gray-800 font-medium">积分余额</span>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1.5">对方手机号</p>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="请输入对方手机号"
                  value={form.targetPhone}
                  onChange={(e) => {
                    setForm({ ...form, targetPhone: e.target.value });
                    setFormError('');
                  }}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-300 pr-9"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  {targetSearching && (
                    <i className="ri-loader-4-line text-gray-400 text-base animate-spin" />
                  )}
                  {!targetSearching && targetNickname && (
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-base" />
                  )}
                  {!targetSearching && targetNotFound && form.targetPhone.trim().length >= 11 && (
                    <i className="ri-close-circle-fill text-rose-400 text-base" />
                  )}
                </span>
              </div>
              {/* 识别到用户 */}
              {targetNickname && (
                <div className="mt-2 flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                    <i className="ri-user-line text-emerald-500 text-xs" />
                  </div>
                  <span className="text-xs text-emerald-700">
                    收款方：<span className="font-semibold">{targetNickname}</span>
                  </span>
                </div>
              )}
              {/* 未找到用户 */}
              {!targetSearching && targetNotFound && form.targetPhone.trim().length >= 11 && (
                <p className="mt-1.5 text-xs text-rose-500">
                  <i className="ri-information-line mr-0.5" />未找到该手机号对应的用户
                </p>
              )}
            </div>

            <div>
              <p className="text-xs text-gray-500 mb-1.5">转账金额（积分）</p>
              <input
                type="number"
                placeholder="请输入转账积分数量"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-rose-300"
              />
            </div>

            {formError && <p className="text-xs text-rose-500">{formError}</p>}

            <button
              onClick={handleOpenPayModal}
              className={`w-full py-3.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                form.targetPhone && form.amount ? 'bg-rose-500 text-white' : 'bg-gray-200 text-gray-400'
              }`}
            >
              下一步
            </button>
          </div>

          {/* Transfer Records */}
          <div className="bg-white rounded-2xl overflow-hidden mb-4">
            <button
              onClick={() => setShowRecords(!showRecords)}
              className="w-full flex items-center justify-between px-4 py-4 cursor-pointer"
            >
              <span className="font-semibold text-gray-900 text-sm">转账记录</span>
              <i className={`${showRecords ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-xl`} />
            </button>
            {showRecords && (
              <div className="divide-y divide-gray-50 border-t border-gray-100">
                {recordsLoading ? (
                  <div className="py-8 text-center text-sm text-gray-400">加载中...</div>
                ) : records.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-400">暂无转账记录</div>
                ) : (
                  records.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${r.delta > 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                          <i className={`text-sm ${r.delta > 0 ? 'ri-arrow-down-circle-line text-emerald-500' : 'ri-arrow-up-circle-line text-rose-400'}`} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-800">{r.remark || (r.delta < 0 ? '转出积分' : '收到积分')}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{formatDate(r.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-sm ${r.delta > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {r.delta > 0 ? '+' : ''}{r.delta.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-300 mt-0.5">余 {r.balance_after.toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showPayModal && (
        <PayPasswordModal
          onConfirm={handleConfirmTransfer}
          onCancel={() => { if (!submitting) setShowPayModal(false); }}
          loading={submitting}
          error={payError}
        />
      )}
    </div>
  );
};

export default TransferPage;
